import { format, parseISO } from 'date-fns';
import { FC, ReactNode } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, DatePicker, FormControl, FormHelperText, HStack, Spinner, Stack, Text } from '@bonusly/design-system';
import { CalendarOutline, CoinPointsOutline } from '@bonusly/ds-icon';

import { FormErrorMessage } from '../../../components/form/form-error-message';
import { FormLabel } from '../../../components/form/form-label';
import { FormSection } from '../../../components/form/form-section';
import { useDebounceValue } from '../../../hooks/use-debounce-value';
import { usePointExpiryImpactPreview } from '../../../queries/point-expiry';
import { toDateInputValue } from '../point-expiry-form-helpers';
import { PointExpiryFormData } from '../schema';

type PointExpiryFormProps = {
	children?: ReactNode;
	form: UseFormReturn<PointExpiryFormData>;
	onSubmit: (data: PointExpiryFormData) => void;
};

// Live impact preview: as the admin edits the dates, this recomputes the company-wide count of
// reward points that will expire when the period ends. The dates are debounced so a keystroke or
// picker change doesn't fire a request on every intermediate value.
//
// Two states are deliberately distinguished, because collapsing them makes the card flicker:
//   * NO count yet (first load for this form) -> spinner. There is nothing truthful to show.
//   * a previous count on screen while a new one loads -> keep the number displayed. The hook's
//     keepPreviousData exists precisely so the value does not blank out between edits; swapping to
//     the spinner on every isFetching would discard it.
//
// The card renders as soon as the admin picks an expiry date, and the query keys off the DEBOUNCED
// date, so between the pick and the debounce settling there is a window with no data and no fetch
// in flight — that window must show the spinner, not an empty card.
//
// A FAILED refetch must never leave the previous count on screen looking authoritative: this number
// is what an admin decides to save a forfeiture schedule against, so a stale count presented as the
// current one is worse than no count. On error the value is replaced by an explicit unavailable
// message.
const ImpactPreview: FC<{ startDate: string; endDate: string }> = ({ startDate, endDate }) => {
	const { t } = useTranslation('admin', { keyPrefix: 'pointExpiryPeriods.form' });
	const debouncedStart = useDebounceValue(startDate, 400);
	const debouncedEnd = useDebounceValue(endDate, 400);

	const {
		data: impactedCount,
		isFetching,
		isError,
	} = usePointExpiryImpactPreview({
		startDate: debouncedStart,
		endDate: debouncedEnd,
	});

	if (!endDate) {
		return null;
	}

	// Awaiting the debounce, or awaiting the first response for these dates.
	const isSettling = debouncedEnd !== endDate || debouncedStart !== startDate;
	// keepPreviousData holds the last successful count even when the current request failed, so the
	// error check has to come first — otherwise the previous dates' number would be shown as if it
	// belonged to the dates now in the form.
	const hasCount = !isError && impactedCount !== undefined;
	const showSpinner = !hasCount && !isError && (isFetching || isSettling);

	return (
		<HStack align="center" spacing={3} p={4} bg="bg.subtle" borderRadius="lg" borderWidth="1px" borderColor="border.subtle" data-testid="impacted-points-count">
			<Box color="points.fg">
				<CoinPointsOutline size="md" color="points.fg" />
			</Box>
			<Stack spacing={0}>
				<Text fontSize={50} color="text.softer" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
					{t('impactHeading')}
				</Text>
				{showSpinner ? (
					<HStack spacing={2} color="text.soft" data-testid="impact-calculating">
						<Spinner size="xs" />
						<Text fontSize={100}>{t('impactCalculating')}</Text>
					</HStack>
				) : isError ? (
					<Text fontSize={100} color="text.soft" data-testid="impact-unavailable">
						{t('impactUnavailable')}
					</Text>
				) : hasCount ? (
					// Dimmed while a fresh count is in flight: the number stays readable but is visibly
					// provisional, rather than vanishing and reflowing the card.
					<Text fontSize={100} fontWeight="semibold" opacity={isFetching || isSettling ? 0.6 : 1} data-testid="impact-count">
						{t('impactCount', { impactedCount })}
					</Text>
				) : null}
			</Stack>
		</HStack>
	);
};

export const PointExpiryForm: FC<PointExpiryFormProps> = ({ children, form, onSubmit }) => {
	const { t } = useTranslation('admin', { keyPrefix: 'pointExpiryPeriods.form' });

	const {
		control,
		formState: { errors },
		handleSubmit,
	} = form;

	// Watch the date fields so the impact preview recomputes as the admin edits them.
	const startDate = useWatch({ control, name: 'startDate' });
	const endDate = useWatch({ control, name: 'endDate' });

	return (
		<Stack as="form" onSubmit={handleSubmit(onSubmit)} spacing={6}>
			<FormSection>
				<HStack spacing={4} align="flex-start">
					<FormControl isInvalid={!!errors.startDate} flex={1}>
						<FormLabel isOptional>{t('startDateLabel')}</FormLabel>
						<Controller
							control={control}
							name="startDate"
							render={({ field }) => (
								<DatePicker
									displayFormat="short"
									value={field.value ? parseISO(toDateInputValue(field.value)) : null}
									onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
									leftIcon={<CalendarOutline color="text.default" />}
									isClearable
									w="full"
								/>
							)}
						/>
						{errors.startDate ? (
							<FormErrorMessage>{errors.startDate?.message}</FormErrorMessage>
						) : (
							<FormHelperText fontSize={50}>{t('startDateHelp')}</FormHelperText>
						)}
					</FormControl>

					<FormControl isInvalid={!!errors.endDate} flex={1}>
						<FormLabel>{t('endDateLabel')}</FormLabel>
						<Controller
							control={control}
							name="endDate"
							render={({ field }) => (
								<DatePicker
									displayFormat="short"
									value={field.value ? parseISO(toDateInputValue(field.value)) : null}
									onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
									leftIcon={<CalendarOutline color="text.default" />}
									isClearable={false}
									w="full"
								/>
							)}
						/>
						{errors.endDate ? (
							<FormErrorMessage>{errors.endDate?.message}</FormErrorMessage>
						) : (
							<FormHelperText fontSize={50}>{t('endDateHelp')}</FormHelperText>
						)}
					</FormControl>
				</HStack>

				<FormControl isInvalid={!!errors.warningDate}>
					<FormLabel>{t('warningDateLabel')}</FormLabel>
					<Controller
						control={control}
						name="warningDate"
						render={({ field }) => (
							<DatePicker
								displayFormat="short"
								value={field.value ? parseISO(toDateInputValue(field.value)) : null}
								onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
								leftIcon={<CalendarOutline color="text.default" />}
								isClearable={false}
								w="full"
							/>
						)}
					/>
					{errors.warningDate ? (
						<FormErrorMessage>{errors.warningDate?.message}</FormErrorMessage>
					) : (
						<FormHelperText fontSize={50}>{t('warningDateHelp')}</FormHelperText>
					)}
				</FormControl>
			</FormSection>

			<ImpactPreview startDate={startDate ?? ''} endDate={endDate ?? ''} />

			{children}
		</Stack>
	);
};
